import type { AssetClientContext } from "@pej/asset-client";
import type { ImageAssetPickerProps } from "@pej/kore/components";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { buildAssetCallbacks } from "./asset-mutations";
import { buildAssetLoader, buildFolderAssetCounter, buildFolderLoader } from "./asset-queries";

/**
 * The workspace asset ceiling the picker reports.
 *
 * The asset service enforces a folder quota but exposes no asset-count quota to
 * clients, so this mirrors the ceiling the integration design documents rather
 * than reading it from anywhere. It will drift silently if the service changes
 * it — recorded as owed work rather than pretended away.
 */
const WORKSPACE_ASSET_CEILING = 100;

/**
 * Everything the picker needs except what only the field knows.
 *
 * `inUseAssetId`, `onSelect`, `onPreview` and `onClose` stay with the caller:
 * they are about *this* field's value and *this* popover's lifetime, and two
 * fields on one template have different answers for each. `title` stays too:
 * it names which slot's picker this is ("Choose a background" / "Choose a
 * logo"), a per-field concern the hook has no way to know.
 */
export type PickerHostProps = Omit<
    ImageAssetPickerProps,
    "inUseAssetId" | "onSelect" | "onPreview" | "onClose" | "title"
>;

export function useAssetPickerProps(ctx: AssetClientContext | null, usedCount: number): PickerHostProps | null {
    const queryClient = useQueryClient();

    return useMemo(() => {
        if (!ctx) return null;
        const callbacks = buildAssetCallbacks(ctx, queryClient, (message) => toast.error(message));
        return {
            loadAssets: buildAssetLoader(ctx, queryClient),
            loadFolders: buildFolderLoader(ctx, queryClient),
            countFolderAssets: buildFolderAssetCounter(ctx, queryClient),
            canManage: true,
            quota: { used: usedCount, limit: WORKSPACE_ASSET_CEILING },
            // Upload is wired per field, because the file lands in whichever
            // field the operator dropped it on.
            onUpload: () => undefined,
            ...callbacks,
        };
    }, [ctx, queryClient, usedCount]);
}
